import { Injectable, signal } from '@angular/core'

import { map, shareReplay, takeUntil } from 'rxjs'

import { Action, BillingAccount, DataQueryProps, Resource } from '@ti/contracts'
import { ReportColumn } from '@ti/core/common/contracts'
import { DataGridColumnActionsFn, DataGridColumnType } from '@ti/core/contracts/data-grid'
import { ListModelConfig } from '@ti/core/models/types'

import { BaseAccountsModel } from '../../common/models/base-accounts.model'

@Injectable()
export class BillingAccountsModel extends BaseAccountsModel<BillingAccount> {
  protected readonly lastBillLabel = signal('ti-admin.billing.last-bill')

  public async toggleActiveStatus(account: BillingAccount, isActive: boolean) {
    try {
      if (isActive) {
        await this.api.ti.accounts.resume(account.id)
      } else {
        await this.api.ti.accounts.suspend(account.id)
      }
      this.reset()
      this.messageService.add({
        summary: await this.languageService.translate('ti-admin.accounts.notifications.updated', {
          name: account.name,
        }),
        life: 5000,
        severity: 'success',
      })
      return true
    } catch (e: any) {
      console.error(e)
      return this.showUnknownErrorNotification()
    }
  }

  public getPreviousCompletedMonthLabel(): string {
    const now = new Date()
    // Go to the first day of current month, then subtract 1 day -> last day of previous month
    const firstOfThisMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))
    const lastOfPrevMonth = new Date(firstOfThisMonth.getTime() - 24 * 60 * 60 * 1000)
    // Format similar to 'month-year' used in the template
    return this.dateFormatService.formatDateInstant(lastOfPrevMonth, 'month-year')
  }

  protected override async init() {
    super.init()
    this.languageService
      .translate$('ti-admin.billing.last-bill')
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (label) => {
        const lastBillingMonthLabel = this.getPreviousCompletedMonthLabel()
        this.lastBillLabel.set(`${label} (${lastBillingMonthLabel})`)
      })
  }

  protected config(): ListModelConfig {
    return {
      name: 'BillingAccountsModel',
      defaultOrderColumn: 'billingStartDate',
      defaultOrderDirection: 'DESC',
      retainCacheUrl: [''],
      gridColumns: [
        {
          field: 'name',
          label: 'ti-admin.accounts.fields.name',
          type: DataGridColumnType.Template,
          sortable: true,
        },
        {
          field: 'id',
          label: 'TSP ID',
          type: DataGridColumnType.Text,
          sortable: true,
        },
        {
          field: 'legalName',
          label: 'ti-admin.accounts.fields.legal-name',
          type: DataGridColumnType.Text,
          sortable: true,
        },
        {
          field: 'devicesCount',
          label: 'ti-admin.billing.billable-devices-count',
          type: DataGridColumnType.Text,
          sortable: false,
        },
        {
          field: 'billingStartDate',
          label: 'ti-admin.billing.billing-start-date',
          format: 'grid-date',
          type: DataGridColumnType.Date,
          sortable: true,
        },
        {
          field: 'billingMode',
          label: 'ti-admin.accounts.fields.billing-mode',
          type: DataGridColumnType.Template,
          sortable: true,
        },
        {
          field: 'billingUsage',
          label: 'ti-admin.billing.last-bill',
          dynamicLabel: this.lastBillLabel,
          type: DataGridColumnType.Template,
          sortable: false,
        },
        {
          field: 'controls',
          label: '',
          type: DataGridColumnType.Template,
          sortable: false,
        },
        {
          field: 'actions',
          label: '',
          type: DataGridColumnType.Actions,
          actions: ((data: BillingAccount) => {
            return this.accessControl
              .massCheck$([
                [Resource.TSP, Action.Update],
                [Resource.TSP, Action.Delete],
                [Resource.Billing, Action.View],
              ])
              .pipe(
                map(([canUpdate, canDelete, canViewBilling]) => {
                  return [
                    {
                      label: 'ti-admin.accounts.page.edit-btn',
                      icon: 'pi pi-pencil',
                      styleClass: `${canUpdate ? '' : 'not-allowed'}`,
                      // rollback once billing moved to separate section
                      // styleClass: `has-bottom-border ${canUpdate ? '' : 'not-allowed'}`,
                      command: () => (canUpdate ? this.openEditDialog(data) : undefined),
                    },
                    {
                      label: 'Last Billing Month Details',
                      icon: 'pi pi-wallet',
                      styleClass: `has-bottom-border ${canViewBilling ? '' : 'not-allowed'}`,
                      command: () => {
                        if (!canViewBilling) {
                          return
                        }
                        const year = new Date().getUTCFullYear()
                        const month = new Date().getUTCMonth()
                        return this.router.navigate(['/billing', data.id, year, month])
                      },
                    },
                    {
                      label: 'ti-admin.accounts.page.delete-btn',
                      icon: 'pi pi-trash',
                      styleClass: `error ${canDelete ? '' : 'not-allowed'}`,
                      command: () => (canDelete ? this.confirmDelete(data) : undefined),
                    },
                  ]
                }),
                takeUntil(this.destroy$),
                shareReplay({ bufferSize: 1, refCount: false }),
              )
          }) as DataGridColumnActionsFn,
        },
      ],
    }
  }

  protected async loadPage(props: DataQueryProps): Promise<BillingAccount[]> {
    return this.api.ti.billing.list(props)
  }

  protected async loadCount() {
    return 0 // Count is not used
  }

  protected override initExportColumns(): ReportColumn[] {
    return [
      {
        field: 'name',
        label: 'ti-admin.accounts.fields.name',
      },
      {
        field: 'email',
        label: 'ti-admin.accounts.fields.email',
      },
      {
        field: 'id',
        label: 'TSP ID',
      },
      {
        field: 'legalName',
        label: 'ti-admin.accounts.fields.legal-name',
      },
      {
        field: 'devicesCount',
        label: 'ti-admin.billing.billable-devices-count',
      },
      {
        field: 'billingStartDate',
        label: 'ti-admin.billing.billing-start-date',
      },
      {
        field: 'billingMode',
        label: 'ti-admin.accounts.fields.billing-mode',
      },
      {
        field: 'billingUsage',
        label: this.lastBillLabel(),
      },
    ]
  }

  protected override async convertDataToExport(data: BillingAccount[]) {
    const translatedModes = await this.languageService.massTranslate({
      annual: `ti-admin.accounts.grid.billing-annual`,
      monthly: `ti-admin.accounts.grid.billing-monthly`,
    })

    return (data ?? []).map((row) => {
      const modeKey = String(row.billingMode)
      const billingModeLabel = translatedModes[modeKey] ?? modeKey

      const lastBill =
        row.billingUsage && typeof row.billingUsage.grandTotal !== 'undefined'
          ? `$${row.billingUsage.grandTotal.toFixed(2)}`
          : '---'

      return {
        name: row.name,
        // Email should be exported as a separate field
        email: row.email ?? '',
        id: row.id,
        legalName: row.legalName ?? '',
        devicesCount: row.devicesCount ?? 0,
        billingStartDate: row.billingStartDate ?? '',
        billingMode: billingModeLabel,
        billingUsage: lastBill,
      }
    })
  }
}
